import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Wordmark } from "@/components/brand/Wordmark";
import { AuthCloseButton } from "@/components/AuthCloseButton";
import { TurnstileWidget } from "@/components/TurnstileWidget";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { usePageMeta } from "@/lib/seo";

export default function SignUp() {
  const { t, i18n } = useTranslation();
  usePageMeta({ title: `${t("auth.signUpTitle")} — AOS Equestrian`, robots: "noindex, nofollow" });
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [tsToken, setTsToken] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const user = await register(name, email, password, {
        accept_platform_terms: acceptTerms,
        // compliance: record the exact language version accepted
        locale: i18n.resolvedLanguage,
        turnstile_token: tsToken,
      });
      toast(t("auth.createdToast", { name: user.name }));
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || t("auth.genericError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-20" data-testid="signup-page">
      <AuthCloseButton />
      <div className="w-full max-w-md flex flex-col gap-10">
        <div className="flex flex-col items-start gap-6">
          <Wordmark />
          <div>
            <h1 className="text-3xl sm:text-4xl font-medium tracking-tight text-[#0A0A0A]">
              {t("auth.signUpTitle")}
            </h1>
            <p className="mt-2 text-sm text-[#737373]">{t("auth.signUpSubtitle")}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-6" noValidate>
          <div className="flex flex-col gap-2">
            <Label htmlFor="signup-name" className="text-sm font-medium">{t("auth.name")}</Label>
            <Input
              id="signup-name"
              data-testid="signup-name-input"
              type="text"
              name="name"
              autoComplete="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("auth.namePlaceholder")}
              className="h-12 rounded-xl border-black/10 focus-visible:ring-1 focus-visible:ring-[#0A0A0A] bg-transparent"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="signup-email" className="text-sm font-medium">{t("auth.email")}</Label>
            <Input
              id="signup-email"
              data-testid="signup-email-input"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("auth.emailPlaceholder")}
              className="h-12 rounded-xl border-black/10 focus-visible:ring-1 focus-visible:ring-[#0A0A0A] bg-transparent"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="signup-password" className="text-sm font-medium">{t("auth.password")}</Label>
            <Input
              id="signup-password"
              data-testid="signup-password-input"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12 rounded-xl border-black/10 focus-visible:ring-1 focus-visible:ring-[#0A0A0A] bg-transparent"
            />
            <span className="text-xs text-[#737373]">{t("auth.passwordHint")}</span>
          </div>

          {/* Legal Engine: aceptación explícita de los términos de plataforma */}
          <div className="flex items-start gap-3 rounded-2xl border border-black/10 px-4 py-3.5">
            <Checkbox
              checked={acceptTerms}
              onCheckedChange={(v) => setAcceptTerms(Boolean(v))}
              data-testid="signup-accept-terms"
              className="mt-0.5"
              aria-label={t("auth.acceptTerms")}
            />
            <button type="button" onClick={() => setAcceptTerms((v) => !v)} className="flex-1 text-left text-sm text-[#0A0A0A] leading-snug">
              {t("auth.acceptTerms")}{" "}
              <Link to="/legal/terms" target="_blank" rel="noopener noreferrer" data-testid="signup-terms-link" className="font-medium text-[#1D4D3B] underline underline-offset-4">
                {t("auth.termsLink")}
              </Link>
            </button>
          </div>

          {error && (
            <p data-testid="signup-error" role="alert" className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
              {error}
            </p>
          )}

          <TurnstileWidget onToken={setTsToken} />

          <Button
            type="submit"
            data-testid="signup-submit-button"
            disabled={submitting || !acceptTerms}
            className="h-12 rounded-full bg-[#0A0A0A] text-white hover:bg-black/80 text-base transition-colors duration-300 disabled:opacity-60"
          >
            {submitting ? `${t("common.loading")}…` : t("auth.signUpBtn")}
          </Button>
        </form>

        <p className="text-sm text-[#737373]">
          {t("auth.haveAccount")}{" "}
          <Link to="/signin" data-testid="signup-to-signin-link" className="font-medium text-[#0A0A0A] underline underline-offset-4 hover:text-[#1D4D3B] transition-colors duration-200">
            {t("auth.signInLink")}
          </Link>
        </p>
      </div>
    </div>
  );
}
